import React from 'react';
import { useTranslation } from 'react-i18next';

import { NOTIF_TYPES } from '@private/notifications';
import { useQuery } from '@tanstack/react-query';
import { TFunction } from 'i18next';

import api from 'api';
import { FILTER_KEYS } from 'api/filters/constants';
import { IRequestFilterQueryProps } from 'api/filters/types';
import { STATUSES } from 'constants/common';
import { defaultQueryRetry } from 'queries';
import { notificationService } from 'utils/notificationService';
import { captureException } from 'utils/sentryLazy';
import { QueryError } from 'utils/types';

import { normalizeQueryKey } from '../utils';

const OPTIONS_LOAD_ERROR_DISMISS_MSEC = 10000;

const showOptionsLoadErrorNotification = (t: TFunction) => {
  notificationService.show({
    dismissIn: OPTIONS_LOAD_ERROR_DISMISS_MSEC,
    message: t('common.filters.optionsLoadErrorNotification'),
    title: t('notifications.errorTitle'),
    toastId: 'filterFieldValuesError',
    type: NOTIF_TYPES.ERROR,
  });
};

const CLIENT_ERROR_RANGE_START = 400;
const SERVER_ERROR_RANGE_START = 500;

// The 400 from CMP-4066 carried a stock Tomcat HTML body, so status, URL and
// content-type must reach Sentry explicitly — the error object alone lacks them
const captureFilterFieldError = (error: QueryError) => {
  captureException(error, {
    extra: {
      contentType: error?.response?.headers?.['content-type'],
      status: error?.response?.status,
      url: error?.config?.url,
    },
  });
};

// 4xx (except 408/429) are deterministic — retrying only delays user feedback
export const filterFieldValuesRetry = (failureCount: number, error: Error) => {
  const status = (error as QueryError)?.response?.status;
  const isDeterministicClientError =
    !!status &&
    status >= CLIENT_ERROR_RANGE_START &&
    status < SERVER_ERROR_RANGE_START &&
    status !== STATUSES.REQUEST_TIMEOUT &&
    status !== STATUSES.TOO_MANY_REQUESTS;

  return !isDeterministicClientError && defaultQueryRetry(failureCount, error);
};

export const useGetFilterFieldData = ({
  enabled,
  field,
  instance,
  keepFieldFilter = false,
  pageFilters,
  prefix,
}: IRequestFilterQueryProps) => {
  const { t } = useTranslation();

  const { [field]: _, ...strippedFilters } = pageFilters?.search || {};

  const filters = React.useMemo(
    () => ({
      ...pageFilters,
      search: keepFieldFilter ? pageFilters?.search || {} : strippedFilters,
    }),
    [pageFilters, strippedFilters, keepFieldFilter],
  );

  const getFilterData = React.useCallback(
    () =>
      prefix && field
        ? api.filters
            .getFilterData({ field, instance, pageFilters: filters, prefix })
            .catch((error: QueryError) => {
              captureFilterFieldError(error);
              showOptionsLoadErrorNotification(t);
              throw error;
            })
        : null,
    [prefix, field, instance, filters, t],
  );

  return useQuery({
    enabled,
    queryFn: getFilterData,
    queryKey: normalizeQueryKey([FILTER_KEYS.ALL, prefix, field, filters]),
    retry: filterFieldValuesRetry,
    // An options-list failure must stay local to the filter control, never
    // reach the app-level ErrorBoundary (full-screen "Something went wrong")
    throwOnError: false,
  });
};

export const useGetAnalyticFilterFieldData = ({
  enabled,
  field,
  pageFilters,
  prefix,
}: IRequestFilterQueryProps) => {
  const { [field]: _, ...searchFilters } = pageFilters?.search || {};

  const filters = React.useMemo(
    () => ({
      ...pageFilters,
      search: searchFilters,
    }),
    [pageFilters, searchFilters],
  );

  const getFilterData = React.useCallback(
    () =>
      prefix && field
        ? api.filters.getAnalyticFilterData({ field, pageFilters: filters, prefix })
        : null,
    [prefix, field, filters],
  );

  return useQuery({
    enabled,
    queryFn: getFilterData,
    queryKey: [FILTER_KEYS.ALL_ANALYTICS, prefix, field, filters],
  });
};
